import { renderCellValue } from '@/components/ui/data-table';
import { Typography } from '@/components/ui/typography';
import type { ComponentData } from '@/types/table';

interface TeamMemberCardProps {
  row: Record<string, unknown>;
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field = ({ label, children }: FieldProps) => (
  <div className="min-w-0">
    <Typography as="p" variant="small" className="text-muted-foreground">
      {label}
    </Typography>
    <div className="break-words">{children}</div>
  </div>
);

/**
 * Sub-lg card equivalent of a TeamMembers table row (DataTable's
 * renderMobileCard slot). Reuses the row's existing cell components —
 * `memberName` carries the edit affordance and `email` carries Resend Invite,
 * both of which are permission-gated upstream, so rendering the components
 * (rather than their `.data` strings) is what keeps the card and the table
 * telling the same truth. A card must show every field its table row shows.
 */
export function TeamMemberCard({ row }: TeamMemberCardProps) {
  const memberName = row.memberName as ComponentData | null;
  const email = row.email as ComponentData | null;

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="team-member-card">
      <Typography as="div" variant="body-semibold" className="break-words">
        {memberName?.component}
      </Typography>
      <Typography as="p" variant="small" className="mt-0.5 text-muted-foreground break-words">
        {String(row.role ?? '')}
        {row.entity ? ` · ${String(row.entity)}` : ''}
      </Typography>

      <div className="mt-3 break-words">{email?.component}</div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Field label="Mobile">{renderCellValue(undefined, row.mobile)}</Field>
        <Field label="Profile Status">
          {renderCellValue('text', row.profileStatus)}
        </Field>
        <Field label="Last Login">{renderCellValue('date', row.lastLogin)}</Field>
      </div>
    </div>
  );
}
